import { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';

import { SEARCH_DEBOUNCE_MS } from '../../../core/constants/forms';
import { IconClose, IconPlus, IconUser } from '../../../common/icons';
import { useCombobox } from '../../../common/hooks/use-combobox';
import { useCreateRecipient, useRecipientsList } from '../contexts/recipients';

import '../../clients/styles/search.css';

// Typeahead de receptoras, ACOTADO a la finca de la sesión (identidad por finca,
// decisión 0023): la búsqueda filtra por `farm_id` y la creación la fija. Así dos
// receptoras con el mismo número en fincas distintas no se mezclan, y el
// recipient_id resuelto siempre pertenece a la finca de la sesión (guard 0023 §4).
// value seleccionado → campo bloqueado + X; "Crear receptora «N»" → POST /recipients.

// Centinela para la opción "Crear receptora «N»" dentro de la lista del combobox.
const CREATE_OPTION = { create: true };

export default function RecipientSearch({ value, onSelect, onClear, farmId, autoFocus = false, placeholder = 'Buscar receptora por número…' }) {
    const [text, setText] = useState('');
    const [debounced, setDebounced] = useState('');
    const { mutateAsync: createRecipient, isPending: creating } = useCreateRecipient();

    useEffect(() => {
        const t = setTimeout(() => setDebounced(text), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [text]);

    const filters = useMemo(() => {
        const out = { limit: 15 };
        if (farmId) out.farm_id = farmId;
        if (debounced.trim()) out.q = debounced.trim();
        return out;
    }, [debounced, farmId]);

    const { data, isFetching } = useRecipientsList(filters, { enabled: !value && !!farmId });
    const items = value ? [] : (data?.items ?? []);

    const q = text.trim();
    const settled = debounced.trim() === q && !isFetching; // resultados al día con lo escrito
    const exact = items.find((r) => (r.number || '').toLowerCase() === q.toLowerCase());
    const canCreate = !!q && settled && !exact;
    const choose = (r) => { setText(''); onSelect?.(r); };
    const createNew = async () => {
        if (!q) return;
        if (!farmId) { toast.error('Falta la finca de la sesión'); return; }
        try {
            const r = await createRecipient({ number: q, farm_id: farmId });
            toast.success('Receptora creada');
            choose(r);
        } catch (err) {
            if (err?.status === 409) toast.error('Ya existe una receptora con ese número en esta finca');
            else toast.error(err?.message || 'No se pudo crear la receptora');
        }
    };

    // La opción "Crear receptora «N»" entra como última opción del listado, así
    // también es alcanzable con flechas + Enter.
    const options = canCreate ? [...items, CREATE_OPTION] : items;
    const cb = useCombobox({
        items: options,
        onChoose: (o) => {
            if (o === CREATE_OPTION) { if (!creating) createNew(); }
            else choose(o);
        },
    });

    if (value) {
        return (
            <div className="search">
                <div className="search__field search__field--selected">
                    <span className="search__field-icon"><IconUser /></span>
                    <span className="search__selected-text">{value.number}{value.name ? ` · ${value.name}` : ''}</span>
                    <button type="button" className="search__clear" onClick={() => { setText(''); onClear?.(); }} aria-label="Cambiar receptora" title="Cambiar">
                        <IconClose />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="search">
            <input
                type="text"
                className="search__input"
                value={text}
                placeholder={placeholder}
                autoFocus={autoFocus}
                role="combobox"
                aria-autocomplete="list"
                aria-expanded={cb.open}
                aria-controls={cb.listId}
                aria-activedescendant={cb.activeId}
                onChange={(e) => { setText(e.target.value); cb.setOpen(true); }}
                onFocus={cb.onFocus}
                onBlur={cb.onBlur}
                onKeyDown={cb.onKeyDown}
            />
            {cb.open && (
                <div className="search__panel" role="listbox" id={cb.listId}>
                    {items.map((r, i) => (
                        <button
                            key={r.id}
                            type="button"
                            id={cb.optionId(i)}
                            role="option"
                            aria-selected={cb.activeIndex === i}
                            className={`search__item ${cb.activeIndex === i ? 'search__item--active' : ''}`}
                            onMouseDown={cb.onOptionMouseDown(r)}
                            onMouseEnter={() => cb.setActive(i)}
                        >
                            <span className="search__item-icon"><IconUser /></span>
                            <span className="search__item-body">
                                <span className="search__item-name">{r.number}{r.name ? ` · ${r.name}` : ''}</span>
                                {r.client?.name && <span className="search__item-sub">{r.client.name}</span>}
                            </span>
                        </button>
                    ))}
                    {canCreate && (
                        <button
                            type="button"
                            id={cb.optionId(items.length)}
                            role="option"
                            aria-selected={cb.activeIndex === items.length}
                            className={`search__item ${cb.activeIndex === items.length ? 'search__item--active' : ''}`}
                            onMouseDown={cb.onOptionMouseDown(CREATE_OPTION)}
                            onMouseEnter={() => cb.setActive(items.length)}
                            disabled={creating}
                        >
                            <span className="search__item-icon"><IconPlus /></span>
                            <span className="search__item-body">
                                <span className="search__item-name">Crear receptora «{q}»</span>
                            </span>
                        </button>
                    )}
                    {q && !settled && <p className="search__hint">Buscando…</p>}
                    {!q && <p className="search__hint">Escribí un número…</p>}
                </div>
            )}
        </div>
    );
}
